import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Clock, FileText } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">InLeads</span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Blog
              </Link>
              <Link to="/contatti" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Contatti
              </Link>
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Accedi all'App
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-[#0047FF] to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            La tua privacy è importante per noi. Scopri come proteggiamo i tuoi dati personali.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Ultimo aggiornamento: 17 Ottobre 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduzione */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduzione</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                InLeads, gestita da Antonio Ceruso International SRL, si impegna a proteggere la privacy 
                degli utenti della nostra piattaforma. Questa Privacy Policy descrive come raccogliamo, 
                utilizziamo e proteggiamo le tue informazioni personali quando utilizzi il nostro sito web 
                e la nostra applicazione.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Utilizzando i nostri servizi, accetti le pratiche descritte in questa Privacy Policy.
              </p>
            </div>

            {/* Dati Raccolti */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Informazioni che Raccogliamo</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Informazioni fornite volontariamente</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>Nome, cognome e informazioni di contatto (email, telefono)</li>
                <li>Informazioni professionali (esperienza, competenze, curriculum)</li>
                <li>Dati dell'agenzia immobiliare (per le agenzie)</li>
                <li>Preferenze di lavoro e obiettivi professionali</li>
                <li>Comunicazioni con il nostro team di supporto</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Informazioni raccolte automaticamente</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>Indirizzo IP e informazioni del dispositivo</li>
                <li>Dati di utilizzo dell'applicazione</li>
                <li>Cookie e tecnologie di tracciamento</li>
                <li>Geolocalizzazione approssimativa (se autorizzata)</li>
              </ul>
            </div>

            {/* Utilizzo dei Dati */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Come Utilizziamo le Tue Informazioni</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizziamo le tue informazioni personali per i seguenti scopi:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Fornire e migliorare i nostri servizi di matching tra candidati e agenzie</li>
                <li>Creare e gestire il tuo profilo utente</li>
                <li>Facilitare la comunicazione tra candidati e agenzie</li>
                <li>Inviare notifiche riguardanti opportunità di lavoro pertinenti</li>
                <li>Fornire supporto clienti e assistenza tecnica</li>
                <li>Analizzare l'utilizzo della piattaforma per miglioramenti</li>
                <li>Rispettare obblighi legali e normativi</li>
                <li>Prevenire frodi e garantire la sicurezza della piattaforma</li>
              </ul>
            </div>

            {/* Condivisione */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Condivisione delle Informazioni</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Non vendiamo le tue informazioni personali. Condividiamo le tue informazioni solo nei seguenti casi:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Con agenzie immobiliari:</strong> quando esprimi interesse per una posizione</li>
                <li><strong>Fornitori di servizi:</strong> per l'hosting, analisi e supporto tecnico</li>
                <li><strong>Obblighi legali:</strong> quando richiesto dalla legge o dalle autorità competenti</li>
                <li><strong>Consenso esplicito:</strong> quando hai dato il tuo consenso specifico</li>
              </ul>
            </div>

            {/* Sicurezza */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Sicurezza dei Dati</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Implementiamo misure di sicurezza tecniche e organizzative appropriate per proteggere 
                le tue informazioni personali contro accesso non autorizzato, alterazione, divulgazione o distruzione:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Crittografia SSL/TLS per le trasmissioni di dati</li>
                <li>Controlli di accesso rigorosi ai nostri sistemi</li>
                <li>Monitoraggio continuo per attività sospette</li>
                <li>Backup regolari e piani di disaster recovery</li>
                <li>Formazione del personale sulla sicurezza dei dati</li>
              </ul>
            </div>

            {/* Diritti */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. I Tuoi Diritti (GDPR)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                In conformità al Regolamento Generale sulla Protezione dei Dati (GDPR), hai i seguenti diritti:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Accesso:</strong> richiedere informazioni sui dati che trattiamo</li>
                <li><strong>Rettifica:</strong> correggere dati inesatti o incompleti</li>
                <li><strong>Cancellazione:</strong> richiedere la cancellazione dei tuoi dati</li>
                <li><strong>Limitazione:</strong> limitare il trattamento dei tuoi dati</li>
                <li><strong>Portabilità:</strong> ricevere i tuoi dati in formato strutturato</li>
                <li><strong>Opposizione:</strong> opporti al trattamento per finalità specifiche</li>
                <li><strong>Revoca consenso:</strong> ritirare il consenso in qualsiasi momento</li>
              </ul>
            </div>

            {/* Retention */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Conservazione dei Dati</h2>
              <p className="text-gray-700 leading-relaxed">
                Conserviamo le tue informazioni personali solo per il tempo necessario agli scopi per cui sono state 
                raccolte, o come richiesto dalla legge. Tipicamente:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
                <li>Profili attivi: per la durata dell'utilizzo del servizio</li>
                <li>Profili inattivi: fino a 3 anni dall'ultima attività</li>
                <li>Dati di comunicazione: fino a 2 anni dalla chiusura dell'account</li>
                <li>Dati di fatturazione: 10 anni per obblighi fiscali</li>
              </ul>
            </div>

            {/* Cookie */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookie e Tecnologie di Tracciamento</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizziamo cookie e tecnologie simili per:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Mantenere la sessione di accesso</li>
                <li>Ricordare le tue preferenze</li>
                <li>Analizzare l'utilizzo del sito</li>
                <li>Personalizzare i contenuti</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Puoi gestire le preferenze sui cookie nelle impostazioni del tuo browser.
              </p>
            </div>

            {/* Minori */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Protezione dei Minori</h2>
              <p className="text-gray-700 leading-relaxed">
                I nostri servizi sono destinati a persone di età superiore ai 16 anni. Non raccogliamo 
                consapevolmente informazioni personali da minori di 16 anni senza il consenso dei genitori.
              </p>
            </div>

            {/* Modifiche */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifiche a questa Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                Potremmo aggiornare questa Privacy Policy periodicamente. Ti notificheremo eventuali modifiche 
                significative tramite email o avviso sulla piattaforma. L'utilizzo continuato dei nostri servizi 
                dopo le modifiche costituisce accettazione della Privacy Policy aggiornata.
              </p>
            </div>

            {/* Contatti */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contattaci</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Per qualsiasi domanda riguardante questa Privacy Policy o per esercitare i tuoi diritti, 
                puoi contattarci:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-[#0047FF]" />
                    <span className="font-semibold">Email:</span>
                    <a href="mailto:info@inleads.it" className="text-[#0047FF] hover:underline">
                      info@inleads.it
                    </a>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-[#0047FF] mt-0.5" />
                    <div>
                      <span className="font-semibold">Titolare del trattamento:</span>
                      <br />
                      Antonio Ceruso International SRL
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Protection Officer */}
            <div className="mb-12 p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-[#0047FF] mb-3">
                Data Protection Officer (DPO)
              </h3>
              <p className="text-gray-700 mb-3">
                Per questioni specifiche sulla protezione dei dati, puoi anche contattare il nostro DPO:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:info@inleads.it" className="text-[#0047FF] hover:underline">info@inleads.it</a><br />
                <strong>Oggetto:</strong> "Att: Data Protection Officer"
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-6">
            Hai domande sulla privacy?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Il nostro team è disponibile per chiarire qualsiasi dubbio
          </p>
          <Link
            to="/contatti"
            className="inline-flex items-center bg-[#0047FF] text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-600 transition-colors"
          >
            <Mail className="mr-2 h-5 w-5" />
            Contattaci
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white border-t border-gray-800">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain brightness-0 invert"
              />
              <span className="text-2xl font-bold">InLeads</span>
            </div>
            <p className="text-gray-400">
              La piattaforma che connette agenzie e talenti nel settore immobiliare
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
              Blog
            </Link>
            <Link to="/formazione" className="text-gray-400 hover:text-white transition-colors">
              Formazione
            </Link>
            <Link to="/contatti" className="text-gray-400 hover:text-white transition-colors">
              Contatti
            </Link>
          </div>

          <div className="text-center mt-8 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-500">
              © 2025 InLeads. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;